// src/pages/pmc/PmcDailyProgressReport.js

import { useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const cx = (...xs) => xs.filter(Boolean).join(" ");

const CONSTRUCTION_TIMELINE_STAGES = [
  { key: "EXCAVATION", label: "Excavation / Site Work" },
  { key: "FOOTING", label: "Footing" },
  { key: "PLINTH_BEAM", label: "Plinth Beam" },
  { key: "COLUMNS_BEAMS", label: "Column & Beam Work" },
  { key: "BRICK_MASONRY", label: "Brick Masonry Work" },
  { key: "PLASTER", label: "Plaster Work" },
  { key: "PEB_INSPECTION", label: "PEB Inspection" },
  { key: "PEB_INSTALLATION", label: "PEB Installation" },
  { key: "COLOUR_WORK", label: "Colour Work" },
  { key: "LOGO_BRANDING", label: "Logo Design & Branding" },
  { key: "FINISHING", label: "Finishing" },
  { key: "COMPLETED", label: "Completed" },
];

function clean(v = "") {
  return String(v || "").trim().toLowerCase();
}

function safeToDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value === "string") {
    const d = dayjs(value);
    return d.isValid() ? d.toDate() : null;
  }
  return null;
}

function formatDate(value) {
  const d = safeToDate(value);
  return d ? dayjs(d).format("DD/MM/YYYY") : "—";
}

function formatDateTime(value) {
  const d = safeToDate(value);
  return d ? dayjs(d).format("DD/MM/YYYY hh:mm A") : "—";
}

function getState(x = {}) {
  return x.state || x.stateName || x.projectState || x.pacsState || "";
}

function getDistrict(x = {}) {
  return (
    x.district ||
    x.districtName ||
    x.projectDistrict ||
    x.pacsDistrict ||
    x.city ||
    ""
  );
}

function isStrictNafed(x = {}) {
  return (
    x.nafedProject === true &&
    x.projectSource === "NAFED" &&
    x.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function tenderMatchesAssignedProject(tender, assignedProjectIds, assignedApplicationIds) {
  const possibleProjectIds = [
    tender.projectId,
    tender.workflowProjectId,
    tender.wfProjectId,
    tender.godownProjectId,
    tender.godownApplicationDocId,
    tender.applicationDocId,
    tender.linkedProjectId,
    tender.linkedGodownProjectId,
  ].filter(Boolean);

  const possibleApplicationIds = [
    tender.applicationId,
    tender.godownApplicationId,
    tender.linkedApplicationId,
    tender.pacsApplicationId,
  ].filter(Boolean);

  return (
    possibleProjectIds.some((id) => assignedProjectIds.has(String(id))) ||
    possibleApplicationIds.some((id) => assignedApplicationIds.has(String(id)))
  );
}

function stageLabel(key) {
  return CONSTRUCTION_TIMELINE_STAGES.find((s) => s.key === key)?.label || key || "—";
}

function Badge({ type = "pending", children }) {
  return <span className={`dpr-badge ${type}`}>{children}</span>;
}

function StatCard({ label, value, note }) {
  return (
    <div className="dpr-stat">
      <label>{label}</label>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}

function exportCsv(rows, selectedTender) {
  const headers = [
    "Sr",
    "Project",
    "PACS",
    "Date",
    "Stage",
    "BOQ Item",
    "Qty Today",
    "Overall Progress",
    "PMC Status",
    "Photos",
    "Contractor Remark",
    "PMC Remark",
  ];

  const data = rows.map((l, idx) => [
    idx + 1,
    selectedTender?.title || "",
    selectedTender?.pacsName || "",
    formatDate(l.date),
    stageLabel(l.stageKey),
    l.boqItemNo ? `#${l.boqItemNo} ${l.boqDescription || ""}` : "",
    l.qtyToday ?? "",
    l.overallProgress != null ? `${l.overallProgress}%` : "",
    l.pmcStatus || "PENDING",
    Array.isArray(l.photos) ? l.photos.length : 0,
    l.remarks || "",
    l.pmcRemark || "",
  ]);

  const csv = [headers, ...data]
    .map((row) => row.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "NAFED_Daily_Progress_Report.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function exportPdf(rows, selectedTender) {
  if (!selectedTender || !rows.length) {
    toast.error("No records to export.");
    return;
  }

  const pdf = new jsPDF("l", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.setFillColor(6, 43, 85);
  pdf.rect(0, 0, pageWidth, 58, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.setTextColor(255, 255, 255);
  pdf.text("NAFED Daily Progress Report - PMC Verification", 36, 34);

  pdf.setTextColor(15, 23, 42);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");

  const meta = [
    `Project: ${selectedTender.title || "—"}`,
    `PACS / Society: ${selectedTender.pacsName || "—"}`,
    `Location: ${selectedTender.district || "—"}, ${selectedTender.state || "—"}`,
    `Generated: ${dayjs().format("DD/MM/YYYY hh:mm A")}`,
  ];

  pdf.text(meta, 36, 78);

  const body = rows.map((l, idx) => {
    const photos = Array.isArray(l.photos) ? l.photos.length : 0;

    return [
      idx + 1,
      formatDate(l.date),
      stageLabel(l.stageKey),
      l.boqItemNo ? `#${l.boqItemNo} ${l.boqDescription || ""}` : "—",
      String(l.qtyToday ?? "—"),
      l.overallProgress != null ? `${l.overallProgress}%` : "—",
      l.pmcStatus || "PENDING",
      photos ? `${photos} file(s)` : "—",
      l.pmcRemark || l.remarks || "—",
    ];
  });

  autoTable(pdf, {
    startY: 135,
    head: [
      [
        "Sr",
        "Date",
        "Stage",
        "BOQ Item",
        "Qty",
        "Overall %",
        "PMC Status",
        "Photos",
        "Remark",
      ],
    ],
    body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: [17, 24, 39],
      lineWidth: 0.4,
      textColor: [15, 23, 42],
    },
    headStyles: {
      fillColor: [226, 232, 240],
      textColor: [7, 31, 69],
      fontStyle: "bold",
      lineColor: [17, 24, 39],
      lineWidth: 0.5,
    },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 60 },
      2: { cellWidth: 120 },
      3: { cellWidth: 180 },
      4: { cellWidth: 45, halign: "right" },
      5: { cellWidth: 60, halign: "right" },
      6: { cellWidth: 75 },
      7: { cellWidth: 55 },
      8: { cellWidth: 190 },
    },
  });

  const safe = String(selectedTender.id || "daily_progress").replace(/[^\w]/g, "_");
  pdf.save(`NAFED_DailyProgress_${safe}.pdf`);
}

export default function PmcDailyProgressReport({ nafedScope }) {
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);
  const [tenders, setTenders] = useState([]);
  const [loadingTenders, setLoadingTenders] = useState(true);
  const [selectedTenderId, setSelectedTenderId] = useState("");

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [pmcStatusFilter, setPmcStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingRemark, setEditingRemark] = useState("");

  const isNafedMode =
    nafedScope?.onlyNafedApply || nafedScope?.strictNafedApplyOnly;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login from PMC / Admin Portal.");
        return;
      }
      setUser(u);
    });

    return () => unsub();
  }, [auth]);

  const getNafedAssignedProjects = async () => {
    const snap = await getDocs(collection(db, "godown_projects"));

    const scopeState = clean(nafedScope?.state);

    const allowedDistricts = (
      nafedScope?.allowedDistricts ||
      nafedScope?.districts ||
      []
    ).map(clean);

    const assignedProjects = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => {
        const stateOk = !scopeState || clean(getState(p)) === scopeState;
        const districtOk =
          !allowedDistricts.length ||
          allowedDistricts.includes(clean(getDistrict(p)));

        return isStrictNafed(p) && stateOk && districtOk;
      });

    const projectIds = new Set(assignedProjects.map((p) => String(p.id)));

    const applicationIds = new Set(
      assignedProjects
        .map((p) => String(p.applicationId || ""))
        .filter(Boolean)
    );

    return { assignedProjects, projectIds, applicationIds };
  };

  const loadTenders = async () => {
    if (!user) return;

    try {
      setLoadingTenders(true);

      const snap = await getDocs(collection(db, "tenders"));
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      list = list.filter((t) => t.awardedVendorUid);

      if (isNafedMode) {
        const { projectIds, applicationIds } = await getNafedAssignedProjects();

        const scopeState = clean(nafedScope?.state);

        const allowedDistricts = (
          nafedScope?.allowedDistricts ||
          nafedScope?.districts ||
          []
        ).map(clean);

        list = list.filter((t) => {
          const linked = tenderMatchesAssignedProject(t, projectIds, applicationIds);

          const tenderStrict =
            t.nafedProject === true &&
            t.projectSource === "NAFED";

          const stateOk = !scopeState || clean(getState(t)) === scopeState;

          const districtOk =
            !allowedDistricts.length ||
            allowedDistricts.includes(clean(getDistrict(t)));

          return linked || (tenderStrict && stateOk && districtOk);
        });
      }

      const finalList = list
        .map((t) => ({
          id: t.id,
          title: t.title || t.projectName || t.workName || "Godown Project",
          pacsName:
            t.ownerCompany ||
            t.organization ||
            t.pacsName ||
            t.societyName ||
            "—",
          district: getDistrict(t) || "—",
          state: getState(t) || "—",
          awardedVendorUid: t.awardedVendorUid || "",
          awardedVendorName:
            t.awardedVendorName ||
            t.vendorName ||
            t.contractorName ||
            "—",
          raw: t,
        }))
        .sort((a, b) => String(a.pacsName || "").localeCompare(String(b.pacsName || "")));

      setTenders(finalList);

      if (finalList.length && !selectedTenderId) {
        setSelectedTenderId(finalList[0].id);
      }

      if (!finalList.length) {
        setSelectedTenderId("");
        setLogs([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load tenders.");
    } finally {
      setLoadingTenders(false);
    }
  };

  useEffect(() => {
    loadTenders();
  }, [user, JSON.stringify(nafedScope || {})]);

  const loadLogs = async () => {
    if (!selectedTenderId) {
      setLogs([]);
      return;
    }

    try {
      setLoadingLogs(true);

      const snap = await getDocs(
        collection(db, "tenders", selectedTenderId, "construction_logs")
      );

      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      list.sort((a, b) => {
        const da = safeToDate(a.date)?.getTime() || 0;
        const dbs = safeToDate(b.date)?.getTime() || 0;
        return dbs - da;
      });

      setLogs(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load daily progress logs.");
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [selectedTenderId]);

  const selectedTender = useMemo(
    () => tenders.find((t) => t.id === selectedTenderId) || null,
    [tenders, selectedTenderId]
  );

  const filteredLogs = useMemo(() => {
    let list = [...logs];

    if (dateFrom) {
      const fromTs = dayjs(dateFrom).startOf("day").valueOf();
      list = list.filter((l) => {
        const d = safeToDate(l.date);
        return !d || d.getTime() >= fromTs;
      });
    }

    if (dateTo) {
      const toTs = dayjs(dateTo).endOf("day").valueOf();
      list = list.filter((l) => {
        const d = safeToDate(l.date);
        return !d || d.getTime() <= toTs;
      });
    }

    if (stageFilter !== "ALL") {
      list = list.filter((l) => l.stageKey === stageFilter);
    }

    if (pmcStatusFilter !== "ALL") {
      list = list.filter((l) => (l.pmcStatus || "PENDING") === pmcStatusFilter);
    }

    const needle = search.trim().toLowerCase();

    if (needle) {
      list = list.filter((l) => {
        const hay = [
          l.stageKey,
          stageLabel(l.stageKey),
          l.boqItemNo,
          l.boqDescription,
          l.remarks,
          l.pmcRemark,
          l.pmcStatus,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [logs, dateFrom, dateTo, stageFilter, pmcStatusFilter, search]);

  const stats = useMemo(() => {
    const total = logs.length;
    const pending = logs.filter((l) => (l.pmcStatus || "PENDING") === "PENDING").length;
    const approved = logs.filter((l) => l.pmcStatus === "APPROVED").length;
    const rejected = logs.filter((l) => l.pmcStatus === "REJECTED").length;

    return { total, pending, approved, rejected };
  }, [logs]);

  const updateLog = async (logId, patch) => {
    if (!selectedTenderId) return;

    try {
      await updateDoc(
        doc(db, "tenders", selectedTenderId, "construction_logs", logId),
        {
          ...patch,
          pmcCheckedAt: serverTimestamp(),
          pmcCheckedByUid: user?.uid || null,
          pmcCheckedByEmail: user?.email || "",
          updatedAt: serverTimestamp(),
        }
      );

      setLogs((prev) => prev.map((l) => (l.id === logId ? { ...l, ...patch } : l)));
    } catch (e) {
      console.error(e);
      toast.error("Failed to update log.");
    }
  };

  const handleApprove = async (log) => {
    await updateLog(log.id, { pmcStatus: "APPROVED" });
    toast.success("Entry approved.");
  };

  const handleReject = async (log) => {
    const reason =
      window.prompt("Reject reason / PMC remark:", log.pmcRemark || "") || "";

    if (!reason) return;

    await updateLog(log.id, {
      pmcStatus: "REJECTED",
      pmcRemark: reason,
    });

    toast.success("Entry rejected.");
  };

  const startEditRemark = (log) => {
    setEditingId(log.id);
    setEditingRemark(log.pmcRemark || log.remarks || "");
  };

  const saveRemark = async () => {
    if (!editingId) return;

    await updateLog(editingId, { pmcRemark: editingRemark });

    setEditingId(null);
    setEditingRemark("");

    toast.success("Remark updated.");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingRemark("");
  };

  const handleDelete = async (log) => {
    const ok = window.confirm(
      "Are you sure you want to delete this daily progress entry?"
    );

    if (!ok || !selectedTenderId) return;

    try {
      await deleteDoc(
        doc(db, "tenders", selectedTenderId, "construction_logs", log.id)
      );

      setLogs((prev) => prev.filter((l) => l.id !== log.id));

      toast.success("Entry deleted.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete entry.");
    }
  };

  const renderStatusBadge = (status) => {
    const s = status || "PENDING";

    if (s === "APPROVED") return <Badge type="approved">APPROVED</Badge>;
    if (s === "REJECTED") return <Badge type="rejected">REJECTED</Badge>;

    return <Badge type="pending">PENDING</Badge>;
  };

  if (!user) {
    return (
      <>
        <Toaster position="top-center" />
        <div style={{ padding: 40, fontWeight: 900 }}>
          Loading PMC Daily Progress Report...
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />

      <div className="dpr-page">
        <style>{`
          .dpr-page {
            display: grid;
            gap: 18px;
            font-family: Inter, "Segoe UI", Arial, sans-serif;
          }

          .dpr-hero {
            background: linear-gradient(135deg, #062b55, #064e3b);
            color: white;
            border-radius: 22px;
            padding: 24px;
            box-shadow: 0 18px 40px rgba(6,43,85,.20);
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 18px;
          }

          .dpr-hero small {
            display: inline-block;
            color: #bbf7d0;
            font-size: 11px;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: .18em;
          }

          .dpr-hero h2 {
            margin: 6px 0 0;
            font-size: 26px;
            font-weight: 950;
          }

          .dpr-hero p {
            margin: 8px 0 0;
            color: #d1fae5;
            font-size: 13px;
            line-height: 1.7;
            max-width: 760px;
          }

          .dpr-btn {
            border: 0;
            border-radius: 11px;
            padding: 10px 14px;
            font-size: 12px;
            font-weight: 950;
            cursor: pointer;
            white-space: nowrap;
          }

          .dpr-btn.white {
            background: white;
            color: #064e3b;
          }

          .dpr-btn.primary {
            background: #062b55;
            color: white;
          }

          .dpr-btn.green {
            background: #16a34a;
            color: white;
          }

          .dpr-btn.red {
            background: #ef4444;
            color: white;
          }

          .dpr-btn.gray {
            background: #e2e8f0;
            color: #071f45;
            border: 1px solid #cbd5e1;
          }

          .dpr-btn:disabled {
            opacity: .45;
            cursor: not-allowed;
          }

          .dpr-stats {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }

          .dpr-stat {
            background: white;
            border: 1px solid #dbe4ef;
            border-radius: 16px;
            padding: 16px;
            box-shadow: 0 8px 22px rgba(15,23,42,.05);
          }

          .dpr-stat label {
            display: block;
            font-size: 10px;
            color: #64748b;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: .12em;
          }

          .dpr-stat b {
            display: block;
            margin-top: 8px;
            color: #071f45;
            font-size: 24px;
            font-weight: 950;
          }

          .dpr-stat small {
            display: block;
            margin-top: 5px;
            color: #64748b;
            font-size: 11px;
            font-weight: 750;
          }

          .dpr-panel {
            background: white;
            border: 1.5px solid #111827;
            border-radius: 18px;
            overflow: hidden;
            box-shadow: 0 8px 22px rgba(15,23,42,.06);
          }

          .dpr-panel-head {
            padding: 15px 18px;
            background: #f8fafc;
            border-bottom: 1.5px solid #111827;
            display: flex;
            justify-content: space-between;
            gap: 14px;
            align-items: center;
          }

          .dpr-panel-head h3 {
            margin: 0;
            color: #071f45;
            font-size: 17px;
            font-weight: 950;
          }

          .dpr-panel-head p {
            margin: 4px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .dpr-filter-grid {
            padding: 16px;
            display: grid;
            grid-template-columns: 1.3fr repeat(5, 1fr);
            gap: 12px;
            background: white;
          }

          .dpr-field {
            display: grid;
            gap: 6px;
          }

          .dpr-field label {
            font-size: 10px;
            color: #64748b;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: .08em;
          }

          .dpr-input {
            height: 40px;
            border: 1px solid #cbd5e1;
            border-radius: 11px;
            padding: 0 10px;
            font-size: 12px;
            font-weight: 800;
            background: white;
            color: #071f45;
            outline: none;
          }

          .dpr-action-row {
            padding: 12px 16px;
            border-top: 1px solid #dbe4ef;
            background: #f8fafc;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
          }

          .dpr-table-wrap {
            overflow-x: auto;
          }

          .dpr-table {
            width: 100%;
            min-width: 1450px;
            border-collapse: collapse;
            font-size: 12px;
          }

          .dpr-table th {
            background: #e2e8f0;
            color: #071f45;
            text-align: left;
            padding: 12px;
            border: 1.5px solid #111827;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: .08em;
            font-weight: 950;
            white-space: nowrap;
          }

          .dpr-table td {
            padding: 12px;
            border: 1.5px solid #111827;
            vertical-align: top;
            background: white;
            color: #0f172a;
            font-weight: 750;
          }

          .dpr-table tbody tr:nth-child(even) td {
            background: #f8fafc;
          }

          .dpr-table tbody tr:hover td {
            background: #eef6ff;
          }

          .dpr-title {
            color: #071f45;
            font-weight: 950;
          }

          .muted {
            color: #64748b;
            font-size: 11px;
            line-height: 1.6;
            font-weight: 750;
          }

          .dpr-badge {
            border-radius: 999px;
            padding: 6px 10px;
            font-size: 10px;
            font-weight: 950;
            display: inline-block;
            white-space: nowrap;
          }

          .dpr-badge.pending {
            background: #fef3c7;
            color: #92400e;
            border: 1px solid #f59e0b;
          }

          .dpr-badge.approved {
            background: #dcfce7;
            color: #166534;
            border: 1px solid #16a34a;
          }

          .dpr-badge.rejected {
            background: #fee2e2;
            color: #991b1b;
            border: 1px solid #ef4444;
          }

          .dpr-photo-list {
            display: grid;
            gap: 5px;
            max-height: 90px;
            overflow: auto;
          }

          .dpr-photo-list a {
            color: #064a9b;
            text-decoration: underline;
            font-weight: 800;
            font-size: 11px;
          }

          .dpr-row-actions {
            display: grid;
            gap: 6px;
          }

          .dpr-empty {
            padding: 40px;
            text-align: center;
            color: #64748b;
            font-size: 13px;
            font-weight: 900;
          }

          .remark-box {
            width: 100%;
            min-height: 74px;
            border: 1px solid #cbd5e1;
            border-radius: 10px;
            padding: 8px;
            font-size: 12px;
            outline: none;
          }

          @media(max-width:1300px) {
            .dpr-stats {
              grid-template-columns: repeat(2, 1fr);
            }

            .dpr-filter-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          @media(max-width:700px) {
            .dpr-stats,
            .dpr-filter-grid {
              grid-template-columns: 1fr;
            }

            .dpr-hero,
            .dpr-panel-head {
              flex-direction: column;
              align-items: flex-start;
            }
          }
        `}</style>

        <section className="dpr-hero">
          <div>
            <small>PMC / Admin - Daily Progress Report</small>
            <h2>Site Execution Daily Logs</h2>
            <p>
              Contractor daily progress entries for PMC verification. In NAFED
              mode, only awarded projects linked to this PMC&apos;s allocated
              state/district scope are visible.
            </p>
          </div>

          <button className="dpr-btn white" onClick={loadTenders}>
            Refresh Projects
          </button>
        </section>

        <section className="dpr-stats">
          <StatCard label="Total Logs" value={stats.total} note="For selected project" />
          <StatCard label="Pending" value={stats.pending} note="Need PMC action" />
          <StatCard label="Approved" value={stats.approved} note="Verified entries" />
          <StatCard label="Rejected" value={stats.rejected} note="Returned entries" />
        </section>

        <section className="dpr-panel">
          <div className="dpr-panel-head">
            <div>
              <h3>Project Selection & Filters</h3>
              <p>Select awarded project and filter daily progress entries.</p>
            </div>
          </div>

          <div className="dpr-filter-grid">
            <div className="dpr-field">
              <label>Project / Tender</label>
              <select
                value={selectedTenderId}
                onChange={(e) => setSelectedTenderId(e.target.value)}
                className="dpr-input"
              >
                {!tenders.length && <option value="">No awarded tenders</option>}
                {tenders.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.pacsName || "PACS"} · {t.title.slice(0, 45)} · [{t.id}]
                  </option>
                ))}
              </select>
            </div>

            <div className="dpr-field">
              <label>Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="dpr-input"
              />
            </div>

            <div className="dpr-field">
              <label>Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="dpr-input"
              />
            </div>

            <div className="dpr-field">
              <label>Stage</label>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="dpr-input"
              >
                <option value="ALL">All Stages</option>
                {CONSTRUCTION_TIMELINE_STAGES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="dpr-field">
              <label>PMC Status</label>
              <select
                value={pmcStatusFilter}
                onChange={(e) => setPmcStatusFilter(e.target.value)}
                className="dpr-input"
              >
                <option value="ALL">All</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            <div className="dpr-field">
              <label>Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dpr-input"
                placeholder="BOQ / stage / remark..."
              />
            </div>
          </div>

          <div className="dpr-action-row">
            <div className="muted">
              Selected Project: <b>{selectedTender?.pacsName || "—"}</b> |{" "}
              Records: <b>{filteredLogs.length}</b>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                className="dpr-btn gray"
                onClick={loadLogs}
                disabled={!selectedTenderId}
              >
                Refresh Logs
              </button>

              <button
                className="dpr-btn green"
                onClick={() => exportCsv(filteredLogs, selectedTender)}
                disabled={!filteredLogs.length}
              >
                Excel / CSV
              </button>

              <button
                className="dpr-btn primary"
                onClick={() => exportPdf(filteredLogs, selectedTender)}
                disabled={!filteredLogs.length || !selectedTender}
              >
                PDF Report
              </button>
            </div>
          </div>
        </section>

        <section className="dpr-panel">
          <div className="dpr-panel-head">
            <div>
              <h3>Daily Progress Entries</h3>
              <p>Professional black-line table with PMC verification actions.</p>
            </div>
          </div>

          {loadingLogs || loadingTenders ? (
            <div className="dpr-empty">Loading daily progress logs...</div>
          ) : !selectedTenderId ? (
            <div className="dpr-empty">Please select project / tender.</div>
          ) : filteredLogs.length === 0 ? (
            <div className="dpr-empty">No daily progress entries found.</div>
          ) : (
            <div className="dpr-table-wrap">
              <table className="dpr-table">
                <thead>
                  <tr>
                    <th>Sr</th>
                    <th>Date</th>
                    <th>Stage</th>
                    <th>BOQ Item</th>
                    <th>Qty</th>
                    <th>Overall %</th>
                    <th>PMC Status</th>
                    <th>Photos</th>
                    <th>Remark / PMC Remark</th>
                    <th>Checked By</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredLogs.map((log, idx) => {
                    const photos = Array.isArray(log.photos) ? log.photos : [];
                    const pmcStatus = log.pmcStatus || "PENDING";
                    const effectiveRemark =
                      editingId === log.id
                        ? editingRemark
                        : log.pmcRemark || log.remarks || "";

                    return (
                      <tr key={log.id}>
                        <td>{idx + 1}</td>

                        <td>
                          <div className="dpr-title">{formatDate(log.date)}</div>
                          <div className="muted">{formatDateTime(log.createdAt)}</div>
                        </td>

                        <td>
                          <div className="dpr-title">{stageLabel(log.stageKey)}</div>
                          <div className="muted">{log.stageKey || "—"}</div>
                        </td>

                        <td>
                          {log.boqItemNo ? (
                            <>
                              <div className="dpr-title">#{log.boqItemNo}</div>
                              <div className="muted">
                                {log.boqDescription || "—"}
                              </div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td style={{ textAlign: "right" }}>
                          {log.qtyToday ?? "—"}
                        </td>

                        <td style={{ textAlign: "right" }}>
                          {log.overallProgress != null
                            ? `${log.overallProgress}%`
                            : "—"}
                        </td>

                        <td>{renderStatusBadge(pmcStatus)}</td>

                        <td>
                          {!photos.length ? (
                            <span className="muted">No photos</span>
                          ) : (
                            <div className="dpr-photo-list">
                              {photos.map((p, i) => (
                                <a
                                  key={i}
                                  href={p.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  download={p.name || `photo_${i + 1}`}
                                >
                                  {i + 1}. {p.name || `Photo ${i + 1}`}
                                </a>
                              ))}
                            </div>
                          )}
                        </td>

                        <td>
                          {editingId === log.id ? (
                            <textarea
                              value={editingRemark}
                              onChange={(e) => setEditingRemark(e.target.value)}
                              className="remark-box"
                            />
                          ) : effectiveRemark ? (
                            <div style={{ maxHeight: 80, overflow: "auto" }}>
                              {effectiveRemark}
                            </div>
                          ) : (
                            <span className="muted">No remark</span>
                          )}
                        </td>

                        <td>
                          <div className="muted">
                            {log.pmcCheckedByEmail || "—"}
                          </div>
                          <div className="muted">
                            {formatDateTime(log.pmcCheckedAt)}
                          </div>
                        </td>

                        <td>
                          <div className="dpr-row-actions">
                            {editingId === log.id ? (
                              <>
                                <button
                                  type="button"
                                  onClick={saveRemark}
                                  className="dpr-btn green"
                                >
                                  Save Remark
                                </button>

                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="dpr-btn gray"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => startEditRemark(log)}
                                className="dpr-btn gray"
                              >
                                Edit Remark
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => handleApprove(log)}
                              className="dpr-btn green"
                            >
                              Approve
                            </button>

                            <button
                              type="button"
                              onClick={() => handleReject(log)}
                              className="dpr-btn red"
                            >
                              Reject
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(log)}
                              className="dpr-btn primary"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}